import axios from 'axios';

const baseUrl = process.env.REACT_APP_API_URL || '/api';

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
});

export type SubjectThreat = 'clear' | 'watch' | 'elevated' | 'high' | 'critical';
export type SubjectStatus = 'active' | 'inactive' | 'archived';

export interface SubjectIndicator {
  label: string;
  kind?: 'positive' | 'watch' | 'warning';
  source?: 'analyst' | 'auto';
}

export interface SubjectAssociation {
  type: string;
  name: string;
  confidence?: number | null;
  notes?: string;
}

export interface SubjectSighting {
  id: number;
  field_report_id?: string | null;
  observed_at?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  place_label?: string | null;
  confidence?: number | null;
  photo_url?: string | null;
}

export interface SubjectCard {
  id: number;
  subject_key: string;
  alias: string;
  subject_type: string;
  cluster_id?: string | null;
  mission_id?: number | null;
  packet_id?: number | null;
  notes: string;
  assessment: string;
  threat_level: SubjectThreat;
  risk_score?: number | null;
  status: SubjectStatus;
  indicators: SubjectIndicator[];
  associations: SubjectAssociation[];
  extra: Record<string, string>;
  first_seen?: string | null;
  last_seen?: string | null;
  sighting_count: number;
  photo_report_id?: string | null;
  photo_url?: string | null;
  locations: string[];
  derived?: {
    sightings?: number;
    areas?: string[];
    area_count?: number;
    locations?: Array<{ latitude: number; longitude: number; observed_at?: string | null; place?: string | null }>;
  };
  sightings: SubjectSighting[];
  created_at?: string | null;
  updated_at?: string | null;
}

export type SubjectCardPatch = Partial<Pick<
  SubjectCard,
  'alias' | 'notes' | 'assessment' | 'threat_level' | 'risk_score' | 'status' | 'indicators' | 'associations' | 'extra'
>>;

type Scope = { missionId: number } | { packetId: number };

function root(scope: Scope): string {
  return 'missionId' in scope
    ? `${baseUrl}/missions/${scope.missionId}/subjects`
    : `${baseUrl}/intel-packets/${scope.packetId}/subjects`;
}

export async function listSubjectCards(scope: Scope): Promise<SubjectCard[]> {
  const response = await axios.get(root(scope), { headers: getAuthHeaders() });
  return response.data.subjects || [];
}

export async function createSubjectCard(
  scope: Scope,
  body: { alias: string; notes?: string },
): Promise<SubjectCard> {
  const response = await axios.post(root(scope), body, { headers: getAuthHeaders() });
  return response.data;
}

export async function openSubjectFromReport(
  scope: Scope,
  reportId: string,
): Promise<SubjectCard> {
  const response = await axios.post(
    `${root(scope)}/from-report`,
    { report_id: reportId },
    { headers: getAuthHeaders() },
  );
  return response.data.subject || (response.data.subjects || [])[0];
}

export async function updateSubjectCard(
  scope: Scope,
  cardId: number,
  patch: SubjectCardPatch,
): Promise<SubjectCard> {
  const response = await axios.patch(`${root(scope)}/${cardId}`, patch, { headers: getAuthHeaders() });
  return response.data;
}

export async function exportSubjectCard(
  scope: Scope,
  cardId: number,
  format: 'html' | 'pdf' = 'html',
  filename = 'subject-card',
): Promise<void> {
  const response = await axios.get(`${root(scope)}/${cardId}/export`, {
    params: { format },
    headers: getAuthHeaders(),
    responseType: 'blob',
  });
  const url = URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.${format}`;
  a.click();
  URL.revokeObjectURL(url);
}
